/**
 * 💳 Checkout Page
 * Complete your order
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, Apple, Lock, Ticket, Truck, MapPin } from 'lucide-react';
import { useCartStore, useUserStore, useOrderStore, useContestStore, useSettingsStore, API_BASE_URL } from '../store';
import styles from './Checkout.module.css';

export function CheckoutPage() {
  const navigate = useNavigate();
  const items = useCartStore((state) => state.items);
  const subtotal = useCartStore((state) => state.subtotal());
  const serviceFee = useCartStore((state) => state.serviceFee);
  const cartTotal = useCartStore((state) => state.total());
  const checkout = useCartStore((state) => state.checkout);
  const setSeatInfo = useUserStore((state) => state.setSeatInfo);
  const addOrder = useOrderStore((state) => state.addOrder);
  const getActiveContest = useContestStore((state) => state.getActiveContest);
  const enterContest = useContestStore((state) => state.enterContest);

  // Delivery settings from admin
  const deliveryEnabled = useSettingsStore((state) => state.deliveryEnabled);
  const deliveryFee = useSettingsStore((state) => state.deliveryFee);

  const [loading, setLoading] = useState(false);
  const [seatSection, setSeatSection] = useState('');
  const [seatRow, setSeatRow] = useState('');
  const [seatNumber, setSeatNumber] = useState('');
  const [selectedPayment, setSelectedPayment] = useState('card');
  const [isDelivery, setIsDelivery] = useState(false);

  // Calculate total with delivery fee if applicable
  const total = isDelivery ? cartTotal + deliveryFee : cartTotal;

  const handlePlaceOrder = async () => {
    setLoading(true);

    // Save seat info
    const seatInfo = seatSection && seatRow
      ? { section: seatSection, row: seatRow, number: seatNumber }
      : null;

    if (seatInfo) {
      setSeatInfo(seatInfo);
    }

    // Create order in history
    const orderId = addOrder({
      items: [...items],
      subtotal,
      serviceFee,
      deliveryFee: isDelivery ? deliveryFee : undefined,
      total,
      status: 'pending',
      pickupWindow: '3',
      isDelivery,
      seatInfo,
    });

    // Auto-enter into active contest
    const activeContest = getActiveContest();
    if (activeContest) {
      enterContest(activeContest.id, orderId);
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: `guest-${Date.now()}`,
          items: items.map(item => ({
            menuItemId: item.menuItemId,
            quantity: item.quantity,
            toppings: item.toppings,
          })),
          seatSection,
          seatRow,
          seatNumber,
        }),
      });

      const data = await response.json();

      if (data.success && data.order) {
        checkout();
        navigate(`/order/${orderId}`);
      } else {
        throw new Error('Order failed');
      }
    } catch (error) {
      // Demo mode - still works with local order
      checkout();
      navigate(`/order/${orderId}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <header className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          <ArrowLeft size={24} />
        </button>
        <h1 className={styles.title}>Checkout</h1>
        <div style={{ width: 24 }} />
      </header>

      <div className={styles.content}>
        {/* Seat Info */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Ticket size={20} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>Your Seat</h2>
            <span className={styles.optionalTag}>Optional</span>
          </div>
          <p className={styles.sectionSubtitle}>Helps us guide you back after pickup!</p>
          
          <div className={styles.seatInputs}>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Section</label>
              <input
                type="text"
                className={styles.input}
                placeholder="e.g. GA-12"
                value={seatSection}
                onChange={(e) => setSeatSection(e.target.value.toUpperCase())}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Row</label>
              <input
                type="text"
                className={styles.input}
                placeholder="e.g. K"
                value={seatRow}
                onChange={(e) => setSeatRow(e.target.value.toUpperCase())}
              />
            </div>
            <div className={styles.inputGroup}>
              <label className={styles.inputLabel}>Seat</label>
              <input
                type="text"
                className={styles.input}
                placeholder="24"
                value={seatNumber}
                onChange={(e) => setSeatNumber(e.target.value)}
              />
            </div>
          </div>
        </section>

        {/* Delivery Option - Only show if enabled in admin */}
        {deliveryEnabled && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <Truck size={20} className={styles.sectionIcon} />
              <h2 className={styles.sectionTitle}>Delivery Option</h2>
            </div>

            <div className={styles.deliveryOptions}>
              <button
                className={`${styles.deliveryOption} ${!isDelivery ? styles.deliveryOptionActive : ''}`}
                onClick={() => setIsDelivery(false)}
              >
                <MapPin size={20} />
                <div className={styles.deliveryDetails}>
                  <span className={styles.deliveryTitle}>Pick Up</span>
                  <span className={styles.deliverySubtitle}>Collect at Window 3</span>
                </div>
                <span className={styles.deliveryPrice}>Free</span>
                {!isDelivery && <span className={styles.checkmark}>✓</span>}
              </button>

              <button
                className={`${styles.deliveryOption} ${isDelivery ? styles.deliveryOptionActive : ''}`}
                onClick={() => setIsDelivery(true)}
                disabled={!seatSection || !seatRow}
              >
                <Truck size={20} />
                <div className={styles.deliveryDetails}>
                  <span className={styles.deliveryTitle}>In-Seat Delivery</span>
                  <span className={styles.deliverySubtitle}>
                    {seatSection && seatRow
                      ? `Delivered to ${seatSection}, Row ${seatRow}`
                      : 'Enter seat info above'}
                  </span>
                </div>
                <span className={styles.deliveryPrice}>+${deliveryFee.toFixed(2)}</span>
                {isDelivery && <span className={styles.checkmark}>✓</span>}
              </button>
            </div>
          </section>
        )}

        {/* Promo Code */}
        <section className={styles.section}>
          <div className={styles.promoRow}>
            <span className={styles.promoIcon}>🎟️</span>
            <span className={styles.promoText}>Have a promo code?</span>
            <button className={styles.promoLink}>[Add]</button>
          </div>
        </section>

        {/* Payment Method */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <CreditCard size={20} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>Payment Method</h2>
          </div>
          
          <button 
            className={`${styles.paymentOption} ${selectedPayment === 'card' ? styles.paymentOptionActive : ''}`}
            onClick={() => setSelectedPayment('card')}
          >
            <CreditCard size={20} />
            <div className={styles.paymentDetails}>
              <span className={styles.paymentTitle}>•••• •••• •••• 4242</span>
              <span className={styles.paymentSubtitle}>Visa</span>
            </div>
            {selectedPayment === 'card' && <span className={styles.checkmark}>✓</span>}
          </button>
          
          <button 
            className={`${styles.paymentOption} ${selectedPayment === 'apple' ? styles.paymentOptionActive : ''}`}
            onClick={() => setSelectedPayment('apple')}
          >
            <Apple size={20} />
            <div className={styles.paymentDetails}>
              <span className={styles.paymentTitle}>Apple Pay</span>
            </div>
            {selectedPayment === 'apple' && <span className={styles.checkmark}>✓</span>}
          </button>
        </section>

        {/* Order Summary */}
        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>Order Summary</h2>
          {items.map((item) => (
            <div key={item.id} className={styles.summaryItem}>
              <span className={styles.summaryQty}>{item.quantity}x</span>
              <span className={styles.summaryName}>{item.name}</span>
              <span className={styles.summaryPrice}>${(item.price * item.quantity).toFixed(2)}</span>
            </div>
          ))}
          <div className={styles.divider} />
          <div className={styles.summaryRow}>
            <span>Subtotal</span>
            <span>${subtotal.toFixed(2)}</span>
          </div>
          <div className={styles.summaryRow}>
            <span>Service Fee</span>
            <span>${serviceFee.toFixed(2)}</span>
          </div>
          {isDelivery && (
            <div className={styles.summaryRow}>
              <span>In-Seat Delivery</span>
              <span>${deliveryFee.toFixed(2)}</span>
            </div>
          )}
          <div className={styles.divider} />
          <div className={styles.summaryRow}>
            <span>Total</span>
            <span className={styles.total}>${total.toFixed(2)}</span>
          </div>
        </section>
      </div>

      {/* Pay Button */}
      <div className={styles.footer}>
        <button 
          className={styles.payButton}
          onClick={handlePlaceOrder}
          disabled={loading}
        >
          <Lock size={18} />
          {loading ? 'Processing...' : `Pay $${total.toFixed(2)}`}
        </button>
        <p className={styles.secureNotice}>
          <Lock size={12} /> Secured by Stripe
        </p>
      </div>
    </div>
  );
}
